import { Link } from 'react-router-dom';
import FadeIn from '@/components/FadeIn';
import { useConsultation } from '@/components/ConsultationModal';
import { IMAGES } from '@/lib/constants';
import { Wine, UtensilsCrossed, PartyPopper, Monitor, Megaphone, ArrowRight, Globe } from 'lucide-react';

const EVENT_LINK = 'https://linktr.ee/theconnectionstrategist?utm_source=ig&utm_medium=social&utm_content=link_in_bio&fbclid=PAZXh0bgNhZW0CMTEAc3J0YwZhcHBfaWQMMjU2MjgxMDQwNTU4AAGnVMEwnjfbNw4sQOhB1BfY_7F3JrSWYmL65ruc-Kiywx5Eb_lIjzp_vNATPNA_aem_0TpLAo0FCXpv-404zbFcmQ';
const IT_LINK = 'https://solacium.us/';

const associates = [
  {
    icon: Wine,
    title: 'Vintage Wine & Champagne',
    scope: 'Retail & Wholesale',
    description: 'Trusted associates with expertise in fine wine and champagne retail and wholesale operations. From inventory management to distribution logistics, our network supports beverage industry clients with specialized knowledge.',
    link: '/wine',
  },
  {
    icon: UtensilsCrossed,
    title: 'Restaurant & Catering',
    scope: 'Hospitality',
    description: 'Strategic associates experienced in restaurant operations, catering management, food cost analysis, and hospitality business development. Support for clients in one of the most demanding industries.',
  },
  {
    icon: PartyPopper,
    title: 'Events & Vendor Connections',
    scope: 'Production & Coordination',
    description: 'Associates skilled in event production, vendor coordination, budget management, and client experience design. Extended support for businesses involved in corporate and social event planning.',
    externalLink: EVENT_LINK,
  },
  {
    icon: Monitor,
    title: 'IT & Programming',
    scope: 'Technology',
    description: 'Trusted technology associates who provide IT infrastructure, software development, systems integration, and digital transformation support. Technical expertise for businesses ready to modernize operations.',
    externalLink: IT_LINK,
  },
  {
    icon: Megaphone,
    title: 'Client Marketing Support',
    scope: 'Growth & Visibility',
    description: 'In addition to financial organization and bookkeeping, selected clients may benefit from coordinated marketing support through trusted associates. Brand development, digital marketing, and client acquisition strategy.',
  },
  {
    icon: Globe,
    title: 'Web Design',
    scope: 'Digital Presence',
    description: 'Trusted web design associates who create professional, conversion-focused websites for businesses ready to strengthen their online presence. From branding to full website development, our network provides quality digital solutions.',
    link: '/web-design',
  },
];

export default function AssociateNetworkPage() {
  const { setOpen } = useConsultation();

  return (
    <div data-testid="associate-network-page">
      {/* HERO */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="network-hero">
        <div className="absolute inset-0">
          <img src={IMAGES.network} alt="Business partnership" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 hero-gradient" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Associate Network</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white max-w-3xl leading-[1.1]">
              Beyond the Books
            </h1>
            <p className="font-body text-base lg:text-lg text-white/60 mt-6 max-w-2xl leading-relaxed">
              William Covington & Associates is a bookkeeping, accounting support, and consulting firm first. For clients who need broader business support, we provide access to a curated network of trusted associates.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* NETWORK VALUE */}
      <section className="py-24 lg:py-32 bg-white" data-testid="network-value">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <FadeIn>
              <div>
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Extended Support</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-wc-black mb-8">
                  Specialized Support Through Trusted Associates
                </h2>
                <div className="space-y-5 font-body text-base text-wc-muted leading-relaxed">
                  <p>
                    Our core expertise is bookkeeping, accounting support, and business consulting. That is our foundation and our focus. However, we understand that business owners often need support beyond the books.
                  </p>
                  <p>
                    Through years of professional relationships, William Covington has built a network of trusted associates with specialized expertise across several industries and disciplines. When clients need extended support, we can connect them with proven professionals who share our standards of quality and reliability.
                  </p>
                  <p>
                    This is not a referral network or a directory. These are carefully vetted professionals we know and trust, and we only make introductions when we believe it will genuinely serve our client's interests.
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={150}>
              <div className="border border-wc-border p-10 lg:p-12 bg-wc-bg-alt">
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-5">How It Works</p>
                <div className="space-y-6">
                  {[
                    { step: '01', text: 'During your regular bookkeeping and consulting engagement, we identify areas where additional support could benefit your business.' },
                    { step: '02', text: 'We match your needs with trusted associates who have proven expertise in the relevant area.' },
                    { step: '03', text: 'We facilitate the introduction and ensure alignment with your business goals and standards.' },
                    { step: '04', text: 'You maintain full control. Associate relationships are always optional and client-directed.' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-5">
                      <span className="font-accent text-xs text-wc-gold font-semibold mt-1">{item.step}</span>
                      <p className="font-body text-sm text-wc-black leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ASSOCIATE AREAS */}
      <section className="py-24 lg:py-32 bg-wc-bg-alt" data-testid="network-associates">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="mb-16">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Areas of Expertise</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black">
                Our Associate Network
              </h2>
            </div>
          </FadeIn>
          <div className="space-y-px bg-wc-border">
            {associates.map((associate, i) => (
              <FadeIn key={i}>
                <div className="bg-white p-10 lg:p-12 grid lg:grid-cols-3 gap-8 items-start">
                  <div className="lg:col-span-1">
                    <div className="flex items-center gap-3 mb-2">
                      <associate.icon className="w-5 h-5 text-wc-gold" strokeWidth={1.5} />
                      <span className="font-accent text-[10px] uppercase tracking-[0.2em] text-wc-muted">{associate.scope}</span>
                    </div>
                    <h3 className="font-heading text-xl sm:text-2xl font-normal text-wc-black">{associate.title}</h3>
                  </div>
                  <div className="lg:col-span-2">
                    <p className="font-body text-sm text-wc-muted leading-relaxed">{associate.description}</p>
                    {associate.link && (
                      <Link
                        to={associate.link}
                        className="inline-flex items-center gap-2 mt-4 font-body text-sm uppercase tracking-[0.1em] text-wc-gold border-b border-wc-gold pb-0.5 hover:text-wc-gold-hover hover:border-wc-gold-hover transition-colors duration-300"
                        data-testid={`associate-link-${associate.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        Learn More <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Link>
                    )}
                    {associate.externalLink && (
                      <a
                        href={associate.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 font-body text-sm uppercase tracking-[0.1em] text-wc-gold border-b border-wc-gold pb-0.5 hover:text-wc-gold-hover hover:border-wc-gold-hover transition-colors duration-300"
                        data-testid={`associate-ext-link-${associate.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        Visit Partner <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </a>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* IMPORTANT NOTE */}
      <section className="py-20 bg-white" data-testid="network-note">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <FadeIn>
            <div className="w-12 h-px bg-wc-gold mx-auto mb-8" />
            <p className="font-body text-base text-wc-muted leading-relaxed italic">
              "Associate network services are extended support options available to existing bookkeeping and consulting clients. They are not standalone offerings and are always secondary to our core financial organization services."
            </p>
            <p className="font-body text-sm font-semibold text-wc-black mt-4">— William Covington & Associates</p>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32" data-testid="network-cta">
        <div className="absolute inset-0">
          <img src={IMAGES.darkTexture} alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-wc-black/85" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-6">Get Started</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-white mb-6">
              Start With the Books.<br />Grow From There.
            </h2>
            <p className="font-body text-base lg:text-lg text-white/55 max-w-xl mx-auto mb-10 leading-relaxed">
              Our associate network is available to bookkeeping and consulting clients. Schedule a consultation to explore how we can support your business.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="bg-wc-gold text-white px-10 py-4 font-body text-sm uppercase tracking-[0.15em] hover:bg-wc-gold-hover transition-colors duration-300"
              data-testid="network-cta-button"
            >
              Schedule a Consultation
            </button>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
